import { useState, useCallback, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import SearchIcon from '@mui/icons-material/Search'
import Typography from '@mui/material/Typography'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import api from 'services/api'
import { useAuth } from 'store/selectors'
import Preview from './Preview'
import ReclipCreatedTimestamp from '../ReclipCreatedTimestamp'
import InfiniteScroll from 'react-infinite-scroll-component'
import CopyToClipboardButton from 'components/core/CopyToClipboard'
import OpenLinkIcon from '@mui/icons-material/OpenInNew'
import TooltipIcon from 'components/core/Tooltip'
import LoadingButton from '@mui/lab/LoadingButton'
import { FileInputIcon } from 'components/core/FileInput'
import useToast from 'hooks/useToast'

const TRIGGER_TYPES = ['laughter']

const ANIMATION_TYPE_MAP = {
  PNG: 'png',
  AI: 'ai',
  ASMR: 'asmr',
  Emoticon: 'emoticon',
}

const ANIMATION_TYPE_LABELS = Object.keys(ANIMATION_TYPE_MAP)
const DEBOUNCE_MS = 300

/**
 * Render API highlight segments for a given path.
 * Falls back to plain text when no highlight data is available.
 */
const Highlighted = ({ highlights, path, fallbackText }) => {
  if (!highlights?.length) return <>{fallbackText ?? ''}</>
  const match = highlights.find((h) => h.path === path)
  if (!match?.texts?.length) return <>{fallbackText ?? ''}</>
  return (
    <>
      {match.texts.map((seg, i) =>
        seg.type === 'hit' ? (
          <mark key={i} style={{ backgroundColor: '#fff59d', padding: 0 }}>
            {seg.value}
          </mark>
        ) : (
          <span key={i}>{seg.value}</span>
        )
      )}
    </>
  )
}

/**
 * Find phrase-match chunks in text for local highlighting.
 * Returns sorted array of { start, end, tier } where:
 *   'exact' = query words appear consecutively (bright yellow)
 *   'near'  = query words appear with slop or last word is prefix-matched (dim yellow)
 */
const findPhraseChunks = (rawQuery, text) => {
  if (!rawQuery?.trim() || !text) return []

  const isQuoted = rawQuery.includes('"')
  const words = rawQuery.replace(/"/g, '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return []

  const esc = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const chunks = []
  let match

  // Tier 1: Exact consecutive phrase
  const exactRe = new RegExp('\\b' + esc.join('\\s+') + '\\b', 'gi')
  while ((match = exactRe.exec(text)) !== null) {
    chunks.push({
      start: match.index,
      end: match.index + match[0].length,
      tier: 'exact',
    })
    if (match.index === exactRe.lastIndex) exactRe.lastIndex++
  }

  // Tier 2: Slop + autocomplete — skip when query was quoted (exact only)
  if (!isQuoted) {
    const nearParts = esc.map((w, i) => (i === esc.length - 1 ? w + '\\w*' : w))
    const nearPattern =
      words.length === 1
        ? '\\b' + nearParts[0]
        : '\\b' + nearParts.join('(?:\\s+\\S+){0,2}\\s+')
    const nearRe = new RegExp(nearPattern, 'gi')
    while ((match = nearRe.exec(text)) !== null) {
      const s = match.index
      const e = s + match[0].length
      const coveredByExact = chunks.some(
        (c) => c.tier === 'exact' && c.start <= s && c.end >= e
      )
      if (!coveredByExact) {
        chunks.push({ start: s, end: e, tier: 'near' })
      }
      if (match.index === nearRe.lastIndex) nearRe.lastIndex++
    }
  }

  return chunks.sort((a, b) => a.start - b.start)
}

/**
 * Local phrase highlighter with two visual tiers.
 * Exact phrase matches get bright yellow; slop/autocomplete matches get a dimmer yellow.
 */
const PhraseHighlighted = ({ query, text }) => {
  if (!text) return null
  if (!query?.trim()) return <>{text}</>

  const chunks = findPhraseChunks(query, text)
  if (chunks.length === 0) return <>{text}</>

  const segments = []
  let lastEnd = 0

  for (const chunk of chunks) {
    if (chunk.start < lastEnd) continue
    if (chunk.start > lastEnd) {
      segments.push(
        <span key={`t-${lastEnd}`}>{text.slice(lastEnd, chunk.start)}</span>
      )
    }
    const bg = chunk.tier === 'exact' ? '#fff59d' : '#fff9c4'
    segments.push(
      <mark
        key={`h-${chunk.start}`}
        style={{ backgroundColor: bg, padding: 0 }}
      >
        {text.slice(chunk.start, chunk.end)}
      </mark>
    )
    lastEnd = chunk.end
  }

  if (lastEnd < text.length) {
    segments.push(<span key={`t-${lastEnd}`}>{text.slice(lastEnd)}</span>)
  }

  return <>{segments}</>
}

const SectionHeader = ({ children }) => (
  <Box
    sx={{
      px: 2,
      py: 1,
      backgroundColor: 'action.hover',
      borderBottomWidth: 1,
      borderBottomColor: 'divider',
      borderBottomStyle: 'solid',
    }}
  >
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
    >
      {children}
    </Typography>
  </Box>
)

const ReclipRow = ({
  item,
  highlights,
  searchTerm,
  useLocalHighlight,
  updateCoverPhoto,
}) => (
  <Box
    sx={{
      p: 2,
      borderBottomColor: 'divider',
      borderBottomWidth: 1,
      borderBottomStyle: 'solid',
      display: 'flex',
    }}
  >
    <Preview
      reclip={item}
      onClick={() =>
        window.open(window.location.origin + '/r/' + item.source_id)
      }
    />
    <Box sx={{ flex: 1, ml: 2 }}>
      <Box>
        <b>Title: </b>
        {(item.title || item.title_auto) &&
          (useLocalHighlight ? (
            <PhraseHighlighted
              query={searchTerm}
              text={item.title || item.title_auto}
            />
          ) : (
            <Highlighted
              highlights={highlights}
              path={item.title ? 'title' : 'title_auto'}
              fallbackText={item.title || item.title_auto}
            />
          ))}
      </Box>
      <Box>
        <b>Username: </b>@{item.username}
      </Box>
      <ReclipCreatedTimestamp
        createdAt={item.created_at}
        clippedAt={item.clipped_at}
      />
      {item.auto_emojis?.length > 0 && (
        <Box>
          <b>Emojis: </b>
          <span style={{ fontSize: '1.3em', letterSpacing: '0.15em' }}>
            {item.auto_emojis.join(' ')}
          </span>
        </Box>
      )}
      {item.best_quote && (
        <Box>
          <b>Best Quote: </b>
          <i>"{item.best_quote}"</i>
        </Box>
      )}
      <Box>
        <b>Transcript </b>
        <CopyToClipboardButton title="Copy transcript" data={item.text} />
        <br />
        {item.text &&
          (useLocalHighlight ? (
            <PhraseHighlighted query={searchTerm} text={item.text} />
          ) : (
            <Highlighted
              highlights={highlights}
              path="transcription.text"
              fallbackText={item.text}
            />
          ))}
      </Box>
    </Box>
    <Box sx={{ mx: 1 }}>
      <TooltipIcon
        Icon={OpenLinkIcon}
        title="watch clip"
        placement="left"
        onClick={() => {
          window.open(window.location.origin + '/r/' + item.source_id)
        }}
      />
      <Box sx={{ mt: 1 }}>
        <CopyToClipboardButton
          title="Copy id"
          placement="left"
          data={item.source_id}
        />
      </Box>
      <Box sx={{ mt: 1 }}>
        <FileInputIcon
          source_id={item.source_id}
          title="replace cover photo"
          placement="left"
          onSelect={(formData) => updateCoverPhoto(item.source_id, formData)}
        />
      </Box>
    </Box>
  </Box>
)

const Search = () => {
  const [inputValue, setInputValue] = useState('')
  const [searchParams, setSearchParams] = useState({
    searchTerm: '',
    page: 0,
    limitSearchToUser: false,
    sortBy: 'mixed',
    captureMode: null,
    captureTrigger: null,
    hasAnimation: false,
    animationType: null,
    animationStyle: null,
    unwatched: null,
  })

  const [topHits, setTopHits] = useState([])
  const [items, setItems] = useState([])
  const [results, setResults] = useState(null)
  const [stylesByType, setStylesByType] = useState({})
  const { user } = useAuth()
  const scrollableDiv = useRef(null)
  const [roundTripTime, setRoundTripTime] = useState(null)
  const [isExporting, setIsExporting] = useState(false)
  const toast = useToast()
  const debounceTimer = useRef(null)

  useEffect(() => {
    api.animatedVideos
      .getStyleSettings()
      .then((styles) => {
        const grouped = {}
        for (const s of styles) {
          const rawType = s.animation_type || 'png'
          const type =
            rawType === 'png' || rawType === 'asmr' || rawType === 'emoticon'
              ? rawType
              : 'ai'
          if (!grouped[type]) grouped[type] = []
          grouped[type].push({ style: s.style, display_name: s.display_name })
        }
        setStylesByType(grouped)
      })
      .catch((err) => {
        console.error('Failed to fetch style settings:', err)
      })
  }, [])

  useEffect(() => {
    const {
      searchTerm,
      page,
      limitSearchToUser,
      sortBy,
      captureMode,
      captureTrigger,
      hasAnimation,
      animationType,
      animationStyle,
      unwatched,
    } = searchParams

    let stale = false
    const now = Date.now()

    api.search
      .searchReclips(searchTerm, page, limitSearchToUser, sortBy, {
        captureMode,
        captureTrigger,
        hasAnimation: hasAnimation || undefined,
        animationType,
        animationStyle,
        unwatched,
      })
      .then((results) => {
        if (stale) return
        setResults(results)
        if (page === 0) {
          scrollableDiv.current.scrollTop = 0
          setTopHits(results.top_hits ?? [])
          setItems(results.hits ?? [])
        } else {
          setItems((items) => [...items, ...(results.hits ?? [])])
        }
        setRoundTripTime(Date.now() - now)
      })

    return () => {
      stale = true
    }
  }, [searchParams])

  const handleSearchTermChange = useCallback((e) => {
    const value = e.target.value
    setInputValue(value)

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }
    debounceTimer.current = setTimeout(() => {
      setSearchParams((params) => ({
        ...params,
        searchTerm: value,
        page: 0,
      }))
    }, DEBOUNCE_MS)
  }, [])

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  const handleToggleLimit = useCallback((event) => {
    setSearchParams((params) => ({
      ...params,
      limitSearchToUser: event.target.checked,
      page: 0,
    }))
  }, [])

  const handleSortByChange = useCallback((event, newValue) => {
    if (newValue !== null) {
      setSearchParams((params) => ({
        ...params,
        sortBy: newValue,
        page: 0,
      }))
    }
  }, [])

  const handleToggleAutoClips = useCallback(() => {
    setSearchParams((params) => ({
      ...params,
      captureMode: params.captureMode === 'auto' ? null : 'auto',
      captureTrigger: null,
      page: 0,
    }))
  }, [])

  const handleTriggerChange = useCallback((trigger) => {
    setSearchParams((params) => ({
      ...params,
      captureTrigger: params.captureTrigger === trigger ? null : trigger,
      page: 0,
    }))
  }, [])

  const handleToggleUnwatched = useCallback(() => {
    setSearchParams((params) => {
      const next =
        params.unwatched === null
          ? true
          : params.unwatched === true
          ? false
          : null
      return { ...params, unwatched: next, page: 0 }
    })
  }, [])

  const handleToggleHasAnimation = useCallback(() => {
    setSearchParams((params) => ({
      ...params,
      hasAnimation: !params.hasAnimation,
      animationType: null,
      animationStyle: null,
      page: 0,
    }))
  }, [])

  const handleAnimationTypeChange = useCallback((registryType) => {
    setSearchParams((params) => ({
      ...params,
      animationType:
        params.animationType === registryType ? null : registryType,
      animationStyle: null,
      page: 0,
    }))
  }, [])

  const handleAnimationStyleChange = useCallback((style) => {
    setSearchParams((params) => ({
      ...params,
      animationStyle: params.animationStyle === style ? null : style,
      page: 0,
    }))
  }, [])

  const handleNextPage = useCallback(() => {
    setSearchParams((params) => ({
      ...params,
      page: params.page + 1,
    }))
  }, [])

  const exportReclips = useCallback(async () => {
    setIsExporting(true)
    const url = await api.search.exportReclips(
      searchParams.searchTerm,
      searchParams.limitSearchToUser
    )
    setIsExporting(false)
    window.open(url, '_blank')
  }, [searchParams])

  const updateCoverPhoto = useCallback(
    async (sourceId, formData) => {
      try {
        const updated = await api.reclips.uploadCoverPhoto({
          sourceId,
          formData,
        })
        const updater = (list) =>
          list.map((item) =>
            item.source_id !== sourceId
              ? item
              : { ...item, cover_photo: updated.cover_photo }
          )
        setItems(updater)
        setTopHits(updater)
        toast({ message: 'cover photo updated' })
      } catch (e) {
        console.log('ERROR', e)
        toast({
          severity: 'error',
          message: e.toString(),
          autoHideDuration: 15000,
        })
      }
    },
    [toast]
  )

  const highlightsMap = results?.highlights ?? {}

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'hidden',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          width: '100%',
          flexShrink: 0,
          borderBottomWidth: 1,
          borderBottomColor: 'divider',
          borderBottomStyle: 'solid',
        }}
      >
        <Box
          sx={{
            px: 2,
            backgroundColor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            '& > input': {
              ml: 2,
              height: '60px',
              flex: 1,
              border: 0,
              outline: 0,
              backgroundColor: 'transparent',
              color: 'text.primary',
              fontSize: '20px',
            },
          }}
        >
          <SearchIcon sx={{ fontSize: 32 }} />
          <input
            type="text"
            value={inputValue}
            onChange={handleSearchTermChange}
            placeholder={
              searchParams.limitSearchToUser
                ? `search @${user.username}'s reclips`
                : 'search reclips'
            }
            autoFocus
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ToggleButtonGroup
              value={searchParams.sortBy}
              exclusive
              onChange={handleSortByChange}
              size="small"
            >
              <ToggleButton value="mixed">Mixed</ToggleButton>
              <ToggleButton value="date">Chronological</ToggleButton>
              <ToggleButton value="relevance">By Relevance</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box>Search @{user.username}'s reclips only</Box>
              <Checkbox
                sx={{ ml: 0.5 }}
                checked={searchParams.limitSearchToUser}
                onChange={handleToggleLimit}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            px: 2,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            borderBottomWidth: 1,
            borderBottomColor: 'divider',
            borderBottomStyle: 'solid',
          }}
        >
          <Chip
            label="Auto Clips"
            size="small"
            color={searchParams.captureMode === 'auto' ? 'primary' : 'default'}
            variant={
              searchParams.captureMode === 'auto' ? 'filled' : 'outlined'
            }
            onClick={handleToggleAutoClips}
          />
          {searchParams.captureMode === 'auto' &&
            TRIGGER_TYPES.map((t) => (
              <Chip
                key={t}
                label={t}
                size="small"
                color={
                  searchParams.captureTrigger === t ? 'secondary' : 'default'
                }
                variant={
                  searchParams.captureTrigger === t ? 'filled' : 'outlined'
                }
                onClick={() => handleTriggerChange(t)}
              />
            ))}
          <Box
            sx={{ mx: 0.5, borderLeft: 1, borderColor: 'divider', height: 24 }}
          />
          <Chip
            label="Has Animation"
            size="small"
            color={searchParams.hasAnimation ? 'primary' : 'default'}
            variant={searchParams.hasAnimation ? 'filled' : 'outlined'}
            onClick={handleToggleHasAnimation}
          />
          {searchParams.hasAnimation &&
            ANIMATION_TYPE_LABELS.map((label) => {
              const registryType = ANIMATION_TYPE_MAP[label]
              const isSelected = searchParams.animationType === registryType
              return (
                <Chip
                  key={label}
                  label={label}
                  size="small"
                  color={isSelected ? 'secondary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  onClick={() => handleAnimationTypeChange(registryType)}
                  sx={{ fontWeight: 700 }}
                />
              )
            })}
          {searchParams.animationType &&
            (stylesByType[searchParams.animationType] || []).length > 0 && (
              <>
                <Box
                  sx={{
                    mx: 0.5,
                    borderLeft: 1,
                    borderColor: 'divider',
                    height: 24,
                  }}
                />
                {(stylesByType[searchParams.animationType] || []).map((s) => (
                  <Chip
                    key={s.style}
                    label={s.display_name}
                    size="small"
                    color={
                      searchParams.animationStyle === s.style
                        ? 'info'
                        : 'default'
                    }
                    variant={
                      searchParams.animationStyle === s.style
                        ? 'filled'
                        : 'outlined'
                    }
                    onClick={() => handleAnimationStyleChange(s.style)}
                  />
                ))}
              </>
            )}
          <Box
            sx={{ mx: 0.5, borderLeft: 1, borderColor: 'divider', height: 24 }}
          />
          <Chip
            label={
              searchParams.unwatched === true
                ? 'Unwatched'
                : searchParams.unwatched === false
                ? 'Watched'
                : 'Unwatched'
            }
            size="small"
            color={searchParams.unwatched !== null ? 'primary' : 'default'}
            variant={searchParams.unwatched !== null ? 'filled' : 'outlined'}
            onClick={handleToggleUnwatched}
            title="Click to cycle: All → Unwatched → Watched → All"
          />
        </Box>
        {results !== null && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              fontStyle: 'italic',
            }}
          >
            <Box sx={{ fontStyle: 'italic' }}>
              Showing {items.length} {items.length === 1 ? 'reclip' : 'reclips'}{' '}
              out of {results.num_hits} (in {roundTripTime} ms)
            </Box>
            <LoadingButton
              sx={{ fontStyle: 'normal' }}
              size="small"
              variant="contained"
              onClick={exportReclips}
              loading={isExporting}
              disabled={
                searchParams.searchTerm.trim() === '' || items.length === 0
              }
            >
              Export
            </LoadingButton>
          </Box>
        )}
      </Box>
      <Box
        sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}
        id="scrollableDiv"
        ref={scrollableDiv}
      >
        {results !== null && (
          <InfiniteScroll
            dataLength={items.length}
            next={items.length === 0 ? undefined : handleNextPage}
            hasMore={items.length < results.num_hits}
            scrollableTarget="scrollableDiv"
            scrollThreshold={0.6}
          >
            {topHits.length > 0 && (
              <>
                <SectionHeader>Top Matches</SectionHeader>
                {topHits.map((item) => (
                  <ReclipRow
                    key={`top-${item.source_id}`}
                    item={item}
                    highlights={highlightsMap[item.source_id]}
                    useLocalHighlight={false}
                    updateCoverPhoto={updateCoverPhoto}
                  />
                ))}
                <SectionHeader>More Results</SectionHeader>
              </>
            )}
            {items.map((item) => (
              <ReclipRow
                key={item.source_id}
                item={item}
                highlights={highlightsMap[item.source_id]}
                searchTerm={searchParams.searchTerm}
                useLocalHighlight={searchParams.sortBy !== 'relevance'}
                updateCoverPhoto={updateCoverPhoto}
              />
            ))}
          </InfiniteScroll>
        )}
      </Box>
    </Box>
  )
}

export default Search
